// Utility functions for handling custom fields

/**
 * Remove duplicate fields based on field_name, prioritizing event-specific fields over global ones
 * @param {Array} fields - Array of field objects
 * @returns {Array} - Deduplicated array of fields
 */
export const deduplicateFields = (fields) => {
  return fields.reduce((acc, field) => {
    // Check for exact field_name match
    let existingFieldIndex = acc.findIndex(f => f.field_name === field.field_name);
    
    // If no exact name match, check for label match to prevent semantic duplicates (e.g., 'Email Address' vs 'Email Address')
    if (existingFieldIndex === -1) {
      // Find a field with the exact same case-insensitive label
      existingFieldIndex = acc.findIndex(f => 
        f.field_label?.trim().toLowerCase() === field.field_label?.trim().toLowerCase()
      );
    }
    
    if (existingFieldIndex === -1) {
      // No duplicate found, add the field
      acc.push(field);
    } else {
      // Duplicate found.
      const existingField = acc[existingFieldIndex];
      
      // We prioritize core fields (e.g. 'email', 'phone') over custom variants (e.g. 'email_address')
      const coreNames = ['full_name', 'email', 'phone', 'organization', 'position'];
      const isExistingCore = coreNames.includes(existingField.field_name);
      const isNewCore = coreNames.includes(field.field_name);

      if (isExistingCore && !isNewCore) {
        // Keep existing core field
        return acc;
      } else if (!isExistingCore && isNewCore) {
        // Replace custom field with core field
        acc[existingFieldIndex] = field;
        return acc;
      }

      // If neither or both are core, fallback to global vs event-specific priority
      if (!existingField.is_global && field.is_global) {
        // Keep existing event-specific field, ignore global duplicate
        return acc;
      } else if (existingField.is_global && !field.is_global) {
        // Replace global field with event-specific field
        acc[existingFieldIndex] = field;
      }
      // If both are global or both are event-specific, keep the first one
    }
    
    return acc;
  }, []);
};

/**
 * Get active and unique fields from fields data
 * @param {Object} fieldsData - Fields data from API
 * @returns {Array} - Array of active, unique fields
 */
export const getActiveUniqueFields = (fieldsData) => {
  const fields = fieldsData?.custom_fields || [];
  
  // Debug in development mode only
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 Processing fields:', {
      total: fields.length,
      duplicates: fields.length - new Set(fields.map(f => f.field_name)).size,
      rawFields: fields.map(f => ({ name: f.field_name, active: f.is_active, type: f.field_type }))
    });
  }
  
  const uniqueFields = deduplicateFields(fields);
  const activeFields = uniqueFields.filter(f => f.is_active);
  
  // Removed: We no longer force full_name to be active if it was explicitly hidden.
  // The frontend should strictly obey the is_active flag from the backend configuration.
  
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 Final form fields:', {
      unique: uniqueFields.length,
      active: activeFields.length,
      fieldNames: activeFields.map(f => f.field_name),
      hasFullName: activeFields.some(f => f.field_name === 'full_name')
    });
  }
  
  return activeFields;
};

/**
 * Get all unique fields (both active and inactive) for admin use
 * @param {Object} fieldsData - Fields data from API
 * @returns {Array} - Array of all unique fields
 */
export const getAllUniqueFields = (fieldsData) => {
  const fields = fieldsData?.custom_fields || [];
  
  // Debug in development mode only
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 Processing ALL fields for admin:', {
      total: fields.length,
      duplicates: fields.length - new Set(fields.map(f => f.field_name)).size,
      rawFields: fields.map(f => ({ name: f.field_name, active: f.is_active, type: f.field_type }))
    });
  }
  
  const uniqueFields = deduplicateFields(fields);
  
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 Final admin fields:', {
      unique: uniqueFields.length,
      fieldNames: uniqueFields.map(f => f.field_name),
      activeCount: uniqueFields.filter(f => f.is_active).length,
      inactiveCount: uniqueFields.filter(f => !f.is_active).length
    });
  }
  
  return uniqueFields;
};

/**
 * Find field by name in fields array
 * @param {Array} fields - Array of field objects
 * @param {string} fieldName - Name of the field to find
 * @returns {Object|null} - Field object or null if not found
 */
export const findFieldByName = (fields, fieldName) => {
  return fields.find(f => f.field_name === fieldName) || null;
};
